import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Briefcase, MapPin, Sparkles, Send, X } from "lucide-react";

/**
 * Professional read-only pop-up showing the full JD.
 * Two CTAs: (1) Apply directly (no AI screening) → onApplyDirect(job)
 *          (2) Screen with AI → closes the dialog so the AI zone can take over.
 */
export default function JobDetailsDialog({ job, open, onOpenChange, onApplyDirect, onScreenWithAI }) {
  if (!job) return null;
  const skills = job.skills || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-offwhite border-navy/20 max-h-[92vh] overflow-y-auto p-0"
        closeClassName="text-white opacity-90 hover:opacity-100 hover:text-white"
        data-testid="job-details-dialog"
      >
        {/* Header block on navy */}
        <div className="bg-navy text-offwhite p-8 grain relative">
          <div className="inline-flex items-center gap-2 label-eyebrow border border-gold/40 px-3 py-1.5">
            <Briefcase size={12} /> {job.industry || "Multi-industry"}
          </div>
          <DialogHeader className="mt-4 text-left">
            <DialogTitle className="serif-display text-3xl md:text-4xl leading-tight">
              {job.title}
            </DialogTitle>
            <DialogDescription className="text-offwhite/70 mt-2 text-sm">
              <span className="inline-flex items-center gap-1 mr-3"><MapPin size={12} /> {job.location || "India"}</span>
              <span>· {job.experience || "Any experience"}</span>
              {job.employment_type ? <span> · {job.employment_type}</span> : null}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6">
          {skills.length ? (
            <div>
              <div className="label-eyebrow text-navy/60">Key skills</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {skills.map((s, i) => (
                  <span key={i} className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-slateBg text-navy/80 border border-navy/10">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <div className="label-eyebrow text-navy/60">Job description</div>
            <div className="mt-3 text-navy/85 leading-relaxed whitespace-pre-line text-[14px]">
              {job.description || "No description provided."}
            </div>
          </div>

          {(job.responsibilities || job.requirements) ? (
            <div className="grid md:grid-cols-2 gap-6">
              {job.responsibilities ? (
                <div>
                  <div className="label-eyebrow text-navy/60">Responsibilities</div>
                  <div className="mt-3 text-navy/85 leading-relaxed whitespace-pre-line text-[13px]">
                    {job.responsibilities}
                  </div>
                </div>
              ) : null}
              {job.requirements ? (
                <div>
                  <div className="label-eyebrow text-navy/60">Requirements</div>
                  <div className="mt-3 text-navy/85 leading-relaxed whitespace-pre-line text-[13px]">
                    {job.requirements}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Sticky footer with CTAs */}
        <div className="bg-white border-t border-navy/10 px-8 py-5 flex flex-col sm:flex-row-reverse items-stretch sm:items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => onApplyDirect?.(job)}
            className="btn-primary inline-flex items-center justify-center gap-2 flex-1 sm:flex-none"
            data-testid="job-details-apply"
          >
            <Send size={13} /> Apply now
          </button>
          <button
            type="button"
            onClick={() => onScreenWithAI?.(job)}
            className="btn-outline inline-flex items-center justify-center gap-2 flex-1 sm:flex-none"
            data-testid="job-details-screen-ai"
          >
            <Sparkles size={13} /> Screen with AI first
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-navy/50 hover:text-navy inline-flex items-center gap-1 text-xs sm:ml-auto"
            data-testid="job-details-close"
          >
            <X size={13} /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
